import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Plus } from '@phosphor-icons/react';
import { PageHeader } from '@/components/core';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';

interface ServiceDeskPageShellProps {
  /** Existing UILM key for the page title. */
  titleKey: string;
  /** Existing UILM key for the "requests" (admin) tab label. */
  requestsTabKey: string;
  /** Whether the user may see everyone's applications; without it only "My Applications" shows. */
  canViewRequests: boolean;
  requests: ReactNode;
  mine: ReactNode;
  /** Shown beside the title; omitted when the user cannot apply. */
  onApply?: () => void;
  /** A "View Policy" link shown beside the title (BYOD and MetLife). */
  policyUrl?: string;
}

/**
 * The layout the five legacy service desk screens share: a title, an optional "requests" tab for
 * reviewers, a "My Applications" tab, and an Apply action.
 */
export const ServiceDeskPageShell = ({
  titleKey,
  requestsTabKey,
  canViewRequests,
  requests,
  mine,
  onApply,
  policyUrl,
}: ServiceDeskPageShellProps) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'requests' | 'mine'>(canViewRequests ? 'requests' : 'mine');
  // If the permission resolves after first render, never leave the user on a tab they cannot see.
  const activeTab = tab === 'requests' && !canViewRequests ? 'mine' : tab;

  return (
    <div className="flex w-full flex-col gap-4">
      {/* On a phone the actions are icon-only buttons. */}
      <PageHeader
        title={t(titleKey)}
        actions={[
          ...(policyUrl
            ? [
                {
                  label: t('VIEW_POLICY'),
                  icon: <FileText className="size-5" />,
                  variant: 'outline' as const,
                  onClick: () => window.open(policyUrl, '_blank', 'noopener,noreferrer'),
                },
              ]
            : []),
          ...(onApply
            ? [{ label: t('APPLY'), icon: <Plus className="size-5" />, onClick: onApply }]
            : []),
        ]}
      />

      <Tabs value={activeTab} onValueChange={(next) => setTab(next as 'requests' | 'mine')}>
        <TabsList className="h-auto w-full overflow-x-auto sm:w-auto">
          {canViewRequests && (
            <TabsTrigger value="requests" className="flex-1 sm:flex-none">
              {t(requestsTabKey)}
            </TabsTrigger>
          )}
          <TabsTrigger value="mine" className="flex-1 sm:flex-none">
            {t('MY_APPLICATIONS')}
          </TabsTrigger>
        </TabsList>
        {canViewRequests && (
          <TabsContent value="requests" className="mt-4">
            {requests}
          </TabsContent>
        )}
        <TabsContent value="mine" className="mt-4">
          {mine}
        </TabsContent>
      </Tabs>
    </div>
  );
};
